import {
  ChangeDetectionStrategy,
  Component,
  inject,
  signal,
  computed,
  ViewChild,
  effect,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Button } from 'primeng/button';
import { OverlayPanel, OverlayPanelModule } from 'primeng/overlaypanel';
import { SelectModule } from 'primeng/select';
import { TooltipModule } from 'primeng/tooltip';
import { StoryDemoFacade } from '@brighthub/story';

@Component({
  selector: 'bhub-demo-step-controls',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    FormsModule,
    Button,
    OverlayPanelModule,
    SelectModule,
    TooltipModule,
  ],
  templateUrl: './demo-step-controls.component.html',
  styleUrl: './demo-step-controls.component.scss',
})
export class DemoStepControlsComponent {
  readonly facade = inject(StoryDemoFacade);

  @ViewChild('settingsPanel') settingsPanel!: OverlayPanel;

  readonly showSettingsPanel = signal(false);
  readonly inlineStepInput = signal<number | null>(null);
  readonly inlineStepError = signal<string | null>(null);

  readonly maxStep = this.facade.maxStep;
  readonly stepLabel = this.facade.stepLabel;
  readonly isAvailable = this.facade.isAvailable;
  readonly isLoading = this.facade.isLoading;
  readonly demoId = this.facade.demoId;
  readonly availableDemos = this.facade.availableDemos;
  readonly mode = this.facade.mode;
  readonly resetLabel = this.facade.resetLabel;
  readonly recommendedStartStep = this.facade.recommendedStartStep;
  readonly isStepSyncPending = this.facade.isStepSyncPending;
  readonly totalStepsLabel = computed(() => {
    const max = this.maxStep();
    return max > 0 ? `${max}` : '—';
  });

  readonly canStepBack = computed(
    () => this.facade.canStep() && this.facade.currentStep() > 1
  );
  readonly canStepForward = computed(() => {
    if (!this.facade.canStep()) return false;
    const max = this.maxStep();
    const cur = this.facade.currentStep();
    return max <= 0 || cur < max;
  });

  /** Whether step-specific actions (reset, go-to-step) are available. */
  readonly canUseStepActions = computed(() => this.mode() === 'story-demo');
  readonly canApplyInlineStep = computed(() => {
    if (!this.isAvailable()) return false;
    if (!this.canUseStepActions()) return false;
    const raw = this.inlineStepInput();
    const max = this.maxStep();
    if (raw == null || Number.isNaN(Number(raw))) return false;
    const step = Math.floor(Number(raw));
    if (!Number.isInteger(step) || step < 1) return false;
    if (max > 0 && step > max) return false;
    return step !== this.facade.currentStep();
  });

  readonly backTooltip = computed(() =>
    !this.canStepBack() ? 'Already at the first step.' : 'Previous step'
  );
  readonly forwardTooltip = computed(() =>
    !this.canStepForward() ? 'Already at the last step.' : 'Next step'
  );
  readonly resetTooltip = computed(() => {
    const s = this.facade.recommendedStartStep();
    return `Reset to recommended start step (Step ${s})`;
  });
  readonly applyTooltip = computed(() =>
    this.canApplyInlineStep() ? 'Apply step' : 'No step change to apply'
  );

  constructor() {
    effect(() => {
      this.inlineStepInput.set(this.facade.currentStep());
    });
  }

  onPrev(): void {
    this.facade.stepBack();
  }

  onNext(): void {
    this.facade.stepForward();
  }

  onReset(): void {
    this.facade.reset();
    this.settingsPanel?.hide();
  }

  onSelectDemo(demoId: string | null): void {
    this.facade.applyDemoId(demoId ?? '');
    this.settingsPanel.hide();
  }

  applyInlineStep(): void {
    const raw = this.inlineStepInput();
    const max = this.maxStep();
    const cur = this.facade.currentStep();
    if (raw == null || isNaN(Number(raw))) {
      this.inlineStepError.set('Enter a step number.');
      return;
    }
    const step = Math.floor(Number(raw));
    if (!Number.isInteger(step) || step < 1) {
      this.inlineStepError.set('Step must be a positive integer.');
      this.inlineStepInput.set(cur);
      return;
    }
    if (max > 0 && step > max) {
      this.inlineStepError.set(`Step must be between 1 and ${max}.`);
      this.inlineStepInput.set(cur);
      return;
    }
    if (step === cur) {
      this.inlineStepError.set('Already at this step.');
      return;
    }
    this.inlineStepError.set(null);
    this.facade.jumpTo(step);
  }

  openSettings(event: Event): void {
    this.inlineStepInput.set(this.facade.currentStep());
    this.inlineStepError.set(null);
    this.settingsPanel?.toggle(event);
  }
}
